import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UNIQUE_DEVICE_ID } from 'constants/authConstants';
import { ORGANISATIONS_QUERY_KEY } from 'constants/queryKeys';
import type {
  IOrganisation,
  IUpdateOrganisationRequest,
} from 'interfaces/IOrganisations';
import OrganisationsService from 'services/OrganisationsService';

interface SaveOrgDefaultWaterRateArgs {
  organisationId: string;
  waterRate: number | null;
}

// The update request with every field as the server has it now, and only
// the default water rate changed.
const buildRequest = (
  organisation: IOrganisation,
  waterRate: number | null
): IUpdateOrganisationRequest => ({
  id: organisation.id,
  orgId: organisation.orgId,
  sourceId: UNIQUE_DEVICE_ID,
  isActive: organisation.isActive,
  partner: organisation.partner,
  partnerId: organisation.partnerId,
  orgName: organisation.orgName,
  address1: organisation.address1,
  address2: organisation.address2,
  city: organisation.city,
  state: organisation.state,
  postcode: organisation.postcode,
  country: organisation.country,
  firstName: organisation.firstName,
  lastName: organisation.lastName,
  preferredName: organisation.preferredName,
  email: organisation.email,
  mobile: organisation.mobile,
  phone: organisation.phone,
  fax: organisation.fax,
  abn: organisation.abn,
  notes: organisation.notes,
  orgSetting: { ...organisation.orgSetting, waterRate },
  userAccesses: organisation.userAccesses,
});

/**
 * Saves the organisation's default water rate (OP-2556). The update endpoint
 * replaces the whole organisation record and has no version check, so the
 * organisation is fetched fresh right before saving and only
 * `orgSetting.waterRate` changes — a copy loaded earlier could otherwise undo
 * someone else's recent edits. A settings-only endpoint would remove that
 * window entirely.
 */
export const useSaveOrgDefaultWaterRate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organisationId,
      waterRate,
    }: SaveOrgDefaultWaterRateArgs) => {
      const organisations = await OrganisationsService.FetchAllOrganisations();
      const organisation = organisations.find(
        (org) => org.id === organisationId
      );
      if (!organisation) throw new Error('Organisation not found');
      return OrganisationsService.UpdateOrganisation(
        buildRequest(organisation, waterRate)
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANISATIONS_QUERY_KEY] });
    },
  });
};
